import { Box, IconButton, Typography } from '@mui/material';

/**
 * Map adjustment types to their corresponding API route paths
 * Based on the routes in backend/api/routes/adjustmentRoutes/index.js
 * and the adjustment types defined in Layout.js
 */
export const adjustmentTypeToRouteMap = {
  // Special case for "All" handled separately in component
  
  // Match exact names from the adjustment types in use
  'Pretax': 'pretax',
  'Payable': 'payable',
  'Permanent': 'permanent',
  'Temporary': 'temp',
  'GS Permanent': 'gs-permanent',
  'GS Temporary': 'gs-temp',
  'TI Permanent': 'ti-perm',
  'TI Temporary': 'ti-temp',
  'Tax Adj. Provision': 'current-provision-basis',
  'Tax Adj. Return': 'current-return-basis',
  'Tax Adj. RTP': 'return-to-provision',
  'Net Operating Loss': 'net-operating-loss',
  'Tax Effected Temporary': 'tax-effected-temp',
  'Valuation Allowance': 'valuation-allowance',
  'Tax Effected Valuation Allowance': 'tax-effected-valuation-allowance',
  'State Pre-App Perm': 'state-permanent',
  'State Pre-App Temp': 'state-temp',
  'State Post-App Perm': 'state-post-app-perm',
  'State Post-App Temp': 'state-post-app-temp',
  'State Tax Effected Perm': 'state-tax-effected-perm',
  'State Tax Effected Temp': 'state-tax-effected-temp',
  'State Tax Adj. Return': 'state-return',
  'State Tax Adj. Provision': 'state-provision',
  'State Tax Adj. RTP': 'state-rtp',
  'State Pre-App NOL': 'state-pre-app-nol',
  'State Post-App NOL': 'state-post-app-nol',
  'State Pre-App Valuation Allowance': 'state-pre-app-valuation-allowance',
  'State Post-App Valuation Allowance': 'state-post-app-valuation-allowance',
  'State Tax Effected Valuation Allowance': 'state-tax-effected-valuation-allowance'
};

/**
 * Standard columns that appear in all adjustment types
 * @param {function} handleEdit - Edit handler
 * @param {function} handleDelete - Delete handler
 * @param {Array} masterEntities - List of all master entities
 * @param {Array} masterAdjustments - List of all master adjustments
 */
const getStandardColumns = (handleEdit, handleDelete, masterEntities = [], masterAdjustments = []) => [
  {
    field: 'AdjustmentCode',
    headerName: 'ADJUSTMENT CODE',
    width: 300,
    renderCell: (params) => {
      // Lookup by AdjustmentCode to get the name
      const adj = masterAdjustments.find(a => a.AdjustmentCode === params.row.AdjustmentCode);
      const name = adj ? adj.AdjustmentName : params.row.AdjustmentName || '';
      const displayValue = name ? `${params.value} - ${name}` : params.value;
      return (
        <strong>
          {displayValue}
        </strong>
      );
    },
  },
  {
    field: 'EntityCode',
    headerName: 'ENTITY CODE',
    width: 120,
  },
  {
    field: 'EntityName',
    headerName: 'ENTITY NAME',
    width: 150,
    renderCell: (params) => {
      // Lookup by EntityCode if possible
      const entity = masterEntities.find(e => e.EntityCode === params.row.EntityCode);
      const name = entity ? entity.EntityName : params.value;
      return (
        <Typography variant="body2">{name}</Typography>
      );
    },
  },
  {
    field: 'SubEntity',
    headerName: 'SUB ENTITY',
    width: 120,
  },
  {
    field: 'FilingCode',
    headerName: 'FILING CODE',
    width: 120,
    renderCell: (params) => {
      // State adjustment types that have filing codes
      const stateTypes = [
        'State Pre-App Perm', 'State Pre-App Temp', 'State Post-App Perm', 'State Post-App Temp',
        'State Tax Effected Temp', 'State Tax Adj. Return', 'State Tax Adj. Provision',
        'State Tax Adj. RTP', 'State Pre-App NOL', 'State Post-App NOL',
        'State Pre-App Valuation Allowance', 'State Post-App Valuation Allowance',
        'State Tax Effected Valuation Allowance'
      ];
      
      const isStateType = stateTypes.includes(params.row.AdjustmentType);
      const displayValue = isStateType ? (params.value || '') : 'Federal';
      
      return (
        <Typography variant="body2">{displayValue}</Typography>
      );
    },
  },
  {
    field: 'ActivityType',
    headerName: 'ACTIVITY TYPE',
    width: 130,
  },
  {
    field: 'Amount',
    headerName: 'AMOUNT',
    width: 120,
    valueFormatter: (params) => {
      if (params.value == null) {
        return '';
      }
      // Round to nearest dollar for display
      const roundedValue = Math.round(params.value);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(roundedValue);
    },
  },
  {
    field: 'USDAmount',
    headerName: 'USD AMOUNT',
    width: 120,
    renderCell: (params) => (
      <Typography variant="body2">
        {params.value != null ? (() => {
          // Round to nearest dollar for display
          const roundedValue = Math.round(params.value);
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(roundedValue);
        })() : ''}
      </Typography>
    ),
  },
  {
    field: 'Tag',
    headerName: 'TAG',
    width: 100,
  },
  {
    field: 'SubTag',
    headerName: 'SUB TAG',
    width: 100,
  },
  {
    field: 'PointInTime',
    headerName: 'POINT IN TIME',
    width: 120,
    disableExport: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    renderCell: (params) => (
      <Box>
        <IconButton size="small" onClick={() => handleEdit(params.row)} title="Edit Adjustment">
          <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
        </IconButton>
        <IconButton size="small" onClick={() => handleDelete(params.row.id)} title="Delete Adjustment">
          <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
        </IconButton>
      </Box>
    ),
  },
];

/**
 * Get columns configuration for all adjustment types
 * All adjustment types now use the same standard columns
 */
export const getColumnsForAdjustmentType = (adjustmentType, handleEdit, handleDelete, masterEntities = [], masterAdjustments = []) => {
  // Simply return the standard columns for all adjustment types
  return getStandardColumns(handleEdit, handleDelete, masterEntities, masterAdjustments);
};
